import { useMemo } from 'react';
import type { Image3D } from '~/domain/patient/image3d';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { fuseGridRow } from '~/hooks/gridFusion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';

export interface ActiveFusionWriter {
    /**
     * Fuses `secondary` onto the pair whose primary image is `primary`,
     * through `registration` when the two frames of reference differ.
     * `primary` names the row. A pair the grid cannot show is refused, the
     * user is told, and nothing changes. Returns whether the pair was
     * fused.
     */
    fuse(
        primary: Image3D,
        secondary: Image3D,
        registration?: RigidRegistration
    ): boolean;
    /** Removes the fusion from the pair whose primary image is `primary`. */
    clear(primary: Image3D): void;
}

/**
 * Writes the fusion of the row whose primary image is `primary` in the
 * dataset registry, through {@link fuseGridRow}. It never writes the
 * sidebar's registration selection.
 */
export function useActiveFusionWriter(): ActiveFusionWriter {
    const patient = useSelectedPatient();

    return useMemo<ActiveFusionWriter>(() => {
        const registry = getDatasetEnvironmentRegistry();
        return {
            fuse: (primary, secondary, registration) =>
                fuseGridRow(
                    registry,
                    patient,
                    primary,
                    secondary,
                    registration
                ),
            clear: (primary) => registry.clearFusion(primary.seriesUid),
        };
    }, [patient]);
}
